import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const BookingCreationPanel = ({ drivers = [], onBookingCreate, loading }) => {
  const [form, setForm] = useState({
    serviceType: 'ride',
    customerName: '',
    customerPhone: '',
    pickupAddress: '',
    dropoffAddress: '',
    driverId: '',
    vehicleType: 'sedan',
    notes: '',
    isUrgent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const availableDrivers = drivers?.filter(d => d?.status === 'idle' || d?.status === 'online');

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.pickupAddress || !form?.dropoffAddress) return;
    setSubmitting(true);
    try {
      await onBookingCreate?.(form);
      setSuccess(true);
      setForm({
        serviceType: 'ride', customerName: '', customerPhone: '',
        pickupAddress: '', dropoffAddress: '', driverId: '',
        vehicleType: 'sedan', notes: '', isUrgent: false,
      });
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Booking creation error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = "w-full px-3 py-2 text-sm bg-surface border border-border rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-colors duration-200";
  const labelClass = "block text-xs font-semibold text-text-secondary uppercase tracking-wide mb-1";

  return (
    <div className="flex flex-col h-full bg-surface rounded-xl border border-border overflow-hidden">
      {/* Panel Header */}
      <div className="px-4 py-3 bg-primary border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Plus" size={18} color="#ffd700" />
          <span className="text-sm font-semibold text-white">Create Booking</span>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Success Banner */}
        {success && (
          <div className="flex items-center space-x-2 p-3 bg-success-50 border border-success-100 rounded-lg">
            <Icon name="CheckCircle" size={16} className="text-success" />
            <span className="text-sm text-success font-medium">Booking created successfully!</span>
          </div>
        )}

        {/* Service Type */}
        <div>
          <label className={labelClass}>Service Type</label>
          <div className="grid grid-cols-2 gap-2">
            {['ride', 'delivery']?.map(type => (
              <button
                key={type}
                type="button"
                onClick={() => handleChange('serviceType', type)}
                className={`flex items-center justify-center space-x-2 py-2.5 rounded-lg border text-sm font-medium transition-all duration-200 ${
                  form?.serviceType === type
                    ? 'bg-primary border-primary text-white' :'bg-surface border-border text-text-secondary hover:border-primary/50'
                }`}
              >
                <Icon name={type === 'ride' ? 'Car' : 'Package'} size={15} />
                <span className="capitalize">{type}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Customer Details */}
        <div className="space-y-3">
          <div>
            <label className={labelClass}>Customer Name</label>
            <input
              type="text"
              placeholder="Full name"
              value={form?.customerName}
              onChange={e => handleChange('customerName', e?.target?.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Customer Phone</label>
            <input
              type="tel"
              placeholder="+254 7XX XXX XXX"
              value={form?.customerPhone}
              onChange={e => handleChange('customerPhone', e?.target?.value)}
              className={inputClass}
            />
          </div>
        </div>

        {/* Locations */}
        <div className="space-y-3">
          <div>
            <label className={labelClass}>Pickup Location</label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2">
                <div className="w-2.5 h-2.5 rounded-full bg-success border-2 border-white shadow" />
              </div>
              <input
                type="text"
                placeholder="Enter pickup address"
                value={form?.pickupAddress}
                onChange={e => handleChange('pickupAddress', e?.target?.value)}
                className={`${inputClass} pl-8`}
                required
              />
            </div>
          </div>
          <div>
            <label className={labelClass}>Drop-off Location</label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2">
                <div className="w-2.5 h-2.5 rounded-full bg-error border-2 border-white shadow" />
              </div>
              <input
                type="text"
                placeholder="Enter drop-off address"
                value={form?.dropoffAddress}
                onChange={e => handleChange('dropoffAddress', e?.target?.value)}
                className={`${inputClass} pl-8`}
                required
              />
            </div>
          </div>
        </div>

        {/* Vehicle Type */}
        <div>
          <label className={labelClass}>Vehicle Type</label>
          <select
            value={form?.vehicleType}
            onChange={e => handleChange('vehicleType', e?.target?.value)}
            className={inputClass}
          >
            <option value="sedan">Sedan</option>
            <option value="suv">SUV</option>
            <option value="van">Van</option>
            <option value="truck">Truck</option>
            <option value="motorcycle">Motorcycle</option>
          </select>
        </div>

        {/* Driver Assignment */}
        <div>
          <label className={labelClass}>Assign Driver</label>
          <select
            value={form?.driverId}
            onChange={e => handleChange('driverId', e?.target?.value)}
            className={inputClass}
          >
            <option value="">Auto-assign nearest driver</option>
            {availableDrivers?.map(driver => (
              <option key={driver?.id} value={driver?.id}>
                {driver?.name} — {driver?.vehicleType} ({driver?.licensePlate})
              </option>
            ))}
          </select>
          <p className="text-xs text-text-muted mt-1">
            {availableDrivers?.length} driver{availableDrivers?.length !== 1 ? 's' : ''} available
          </p>
        </div>

        {/* Notes */}
        <div>
          <label className={labelClass}>Notes</label>
          <textarea
            placeholder="Special instructions..."
            value={form?.notes}
            onChange={e => handleChange('notes', e?.target?.value)}
            rows={2}
            className={`${inputClass} resize-none`}
          />
        </div>

        {/* Urgent Flag */}
        <div
          onClick={() => handleChange('isUrgent', !form?.isUrgent)}
          className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all duration-200 ${
            form?.isUrgent
              ? 'bg-error-50 border-error-100' :'bg-surface border-border hover:border-error/40'
          }`}
        >
          <div className="flex items-center space-x-2">
            <Icon name="AlertTriangle" size={16} className={form?.isUrgent ? 'text-error' : 'text-text-muted'} />
            <div>
              <p className={`text-sm font-medium ${form?.isUrgent ? 'text-error' : 'text-text-secondary'}`}>
                Mark as Urgent
              </p>
              <p className="text-xs text-text-muted">Priority escalation + alerts</p>
            </div>
          </div>
          <div className={`w-10 h-5 rounded-full transition-colors duration-200 relative ${form?.isUrgent ? 'bg-error' : 'bg-border'}`}>
            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${form?.isUrgent ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </div>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting || !form?.pickupAddress || !form?.dropoffAddress}
          className="w-full py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2"
        >
          {submitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>Creating...</span>
            </>
          ) : (
            <>
              <Icon name="Send" size={16} />
              <span>Dispatch Order</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default BookingCreationPanel;
